import { RawAdobeAnalytics } from './../../mediacorp/types'
import { of } from 'rxjs'
import { catchError, mergeMap, switchMap } from 'rxjs/operators'
import ConfigHelper from 'framework/helpers/config'
import DataHelper from 'framework/helpers/data'
import { serialize } from 'helpers/index.js'
import Fetch from './fetch'
import { Constants } from 'clients/amdocsDemo'
import { Config, OtherApis } from '../../../..'
import {
    EVENT,
    GetVisitorIdParams,
    ChannelAnalytics,
    CONTENT_TYPE,
    GetVisitorIdResponse,
} from './types'
import { visitorDataFactory } from '../factories'
import { getSessionId } from '../utils'

/**
 * Deals with Playback requirements (session creation, tracking etc)
 */
export default class PlayerAnalyticsApi extends Fetch {
    _visitorIdCached?: string
    _sessionId?: string

    constructor(config: Config, otherApis: OtherApis) {
        super(config, otherApis)
        this._visitorIdCached = undefined
    }

    static PLAYER_ANALYTICS_TAG = '[_PLAYER_ANALYTICS_]'

    static EVENT = EVENT

    static CONTENT_TYPE = CONTENT_TYPE

    /**
     * Sends request to create session whenever user plays any video
     */
    createSession({
        contentType = PlayerAnalyticsApi.CONTENT_TYPE.LIVE,
        channelAnalytics,
        customMetadata,
        playbackUrl,
        trackingSessionId,
        deviceTypeId,
    }: {
        contentType?: CONTENT_TYPE
        channelAnalytics: ChannelAnalytics
        customMetadata?: RawAdobeAnalytics
        playbackUrl?: string
        trackingSessionId?: string
        deviceTypeId?: string
    }) {
        if (!this._isEnabled()) {
            this.logger.warn('Adobe is not enabled')
            return of(false)
        }

        if (!channelAnalytics) {
            this.logger.warn('Adobe is enabled however metadatas are missing')
            return of(false)
        }

        this._clearSessionId()

        return this._getUserVisitorID().pipe(
            mergeMap((visitorId) => {
                this.logger.info(
                    PlayerAnalyticsApi.PLAYER_ANALYTICS_TAG,
                    'Sending Adobe Create Session event...'
                )
                const { analyticsApiUrl, trackingServer, organisationId, reportSuit } =
                    ConfigHelper.getInstance().getConfig('adobeAnalytics')

                const { mediaPlayer, platform } = this._getPlatformDetauks()

                const body = {
                    eventType: PlayerAnalyticsApi.EVENT.SESSION_START,
                    playerTime: {
                        playhead: 0,
                        ts: Date.now(),
                    },
                    params: {
                        'media.playerName': mediaPlayer,
                        'analytics.trackingServer': trackingServer,
                        'analytics.reportSuite': reportSuit,
                        'analytics.visitorId': visitorId,
                        'media.contentType': contentType,
                        'media.length': 0, // TODO: change for a different type of media
                        'media.id': channelAnalytics.mediaId,
                        'visitor.marketingCloudOrgId': organisationId,
                        'media.name': channelAnalytics.mediaName,
                        'media.channel': platform,
                        'media.sdkVersion': 'va-api-0.0.0',
                        'analytics.enableSSL': true,
                    },
                    // This QOE is not mandatory
                    /*qoeData: {
                        'media.qoe.bitrate': '<your param-value>',
                        'media.qoe.droppedFrames': '<your param-value>',
                        'media.qoe.framesPerSecond': '<your param-value>',
                        'media.qoe.timeToStart': '<your param-value>',
                    },*/
                    customMetadata: {
                        ...customMetadata?.adobe,
                        'mcs.sdk4.channel': customMetadata?.adobe?.['mcs.sdk4.channel'].replace(
                            '__PLATFORM_NAME__',
                            platform
                        ),
                        'mcs.sdk4.custompagename': customMetadata?.adobe?.[
                            'mcs.sdk4.custompagename'
                        ].replace('__PLATFORM_NAME__', platform),
                        'mcs.sdk4.pagename': customMetadata?.adobe?.['mcs.sdk4.pagename'].replace(
                            '__PLATFORM_NAME__',
                            platform
                        ),
                        'mcs.sdk4.mediaseriesname': 'NA',
                        'mcs.sdk4.mediatype': 'Video',
                        'a.media.mediaduration': '0',
                        'mcs.sdk4.houseid': 'NA',
                        'mcs.sdk4.masrefid': 'NA',
                        'a.media.playername': mediaPlayer,
                        'mcs.sdk4.mediaplayer': mediaPlayer,
                        'mcs.sdk4.pageurlevar': playbackUrl,
                        'mcs.sdk4.newrepeat': 'New',
                        'mcs.sdk4.ssoid': trackingSessionId,
                        'mcs.sdk4.mobiledeviceid': deviceTypeId,
                        'a.media.view': 'true',
                    },
                }

                return this.fetch({
                    url: `${analyticsApiUrl}/sessions`,
                    headers: {
                        // 'User-Agent': this.getUserAgent(),
                        'Content-Type': 'application/json',
                    },
                    retrieveResponseHeaders: true,
                    method: 'POST',
                    body,
                    log: 'CREATE ADOBE SESSION',
                }).pipe(
                    mergeMap(({ headers = {} }) => {
                        const sessionId = getSessionId(headers.location as string)
                        this._saveSessionId(sessionId)
                        this.logger.info(
                            `${PlayerAnalyticsApi.PLAYER_ANALYTICS_TAG} SESSION created...'`,
                            sessionId
                        )
                        return of(true)
                    }),
                    catchError(() => {
                        this.logger.warn('Sesion Creation Error')
                        return of(false)
                    })
                )
            })
        )
    }

    getUserAgent() {
        const { userAgent } = ConfigHelper.getInstance().getConfig('adobeAnalytics')
        const { adobeUserAgent } = ConfigHelper.getInstance().getConfig('platformConfig')
        return adobeUserAgent || userAgent
    }

    /**
     * Get the keep alive period.
     */
    getKeepAlivePeriod() {
        return this._isEnabled()
            ? ConfigHelper.getInstance().getConfig('adobeAnalytics').pingPeriodMsecs || 10000
            : null
    }

    /**
     * Sends a request for a specific player event
     */
    reportSessionEvent(event: EVENT, playPositionSecs: number | any) {
        if (!this._isEnabled()) {
            return of(false)
        }

        const sessionId = this._getSessionId()
        if (!sessionId) {
            this.logger.info(
                PlayerAnalyticsApi.PLAYER_ANALYTICS_TAG,
                `Reporting ${event} not possible as no existing session`
            )
            return of(false)
        }
        if (event === PlayerAnalyticsApi.EVENT.SESSION_COMPLETE) {
            this._clearSessionId()
        }

        this.logger.info(
            PlayerAnalyticsApi.PLAYER_ANALYTICS_TAG,
            `Reporting ${event} position : ${playPositionSecs}`
        )

        const { analyticsApiUrl } = ConfigHelper.getInstance().getConfig('adobeAnalytics')
        return this.fetch({
            url: `${analyticsApiUrl}/sessions/${sessionId}/events`,
            headers: {
                // 'User-Agent': this.getUserAgent(),
                'Content-Type': 'application/json',
            },
            body: {
                playerTime: {
                    playhead: playPositionSecs?.timeOffset || playPositionSecs,
                    ts: Date.now(),
                },
                eventType: event,
            },
            method: 'POST',
            log: `ADOBE REPORT SESSION - ${event}`,
        }).pipe(
            mergeMap(() => of(true)),
            catchError(() => {
                this.logger.warn('Sesion Event error')
                return of(false)
            })
        )
    }

    /**
     * Send request to generate MID (Marketing Cloud Visitor ID)
     * Visitor ID will be used to calculate “Unique Visitors“ metric in the Adobe Reports
     */
    _getUserVisitorID() {
        const { analyticsUserUrl, organisationId, apiVersion } =
            ConfigHelper.getInstance().getConfig('adobeAnalytics')

        if (this._visitorIdCached) {
            // According the Adobe specification, the visitor ID should be renewed only once at boot
            this.logger.info('Visitor ID already retrieved, renewed only once at boot')
            return of(this._visitorIdCached)
        }

        const storeData = DataHelper.getInstance().getData(
            DataHelper.STORE_KEY.PLAYER_ANALYTICS_KEYS
        )

        let params: GetVisitorIdParams = {
            d_orgid: organisationId,
            d_ver: apiVersion,
        }

        if (storeData) {
            const parsedStoredData: ReturnType<typeof visitorDataFactory> = JSON.parse(storeData)
            params = {
                ...params,
                d_mid: parsedStoredData.visitorId,
                d_blob: parsedStoredData.blob,
                dcs_region: parsedStoredData.region,
            }
        }

        return this.fetch<GetVisitorIdResponse>({
            url: `${analyticsUserUrl}${serialize(params)}`,
            headers: {
                // 'User-Agent': this.getUserAgent(),
                'Content-Type': 'application/json',
            },
            method: 'GET',
            log: storeData
                ? 'GET ADOBE ANALYTICS ID - OTHER TRY'
                : 'GET ADOBE ANALYTICS ID - INITIAL TRY',
        }).pipe(
            mergeMap(({ response = null }) => {
                const visitorData = visitorDataFactory(response)
                this._visitorIdCached = visitorData.visitorId
                return DataHelper.getInstance().storeData([
                    [DataHelper.STORE_KEY.PLAYER_ANALYTICS_KEYS, JSON.stringify(visitorData)],
                ])
            }),
            switchMap(() => of(this._visitorIdCached))
        )
    }

    /**
     * Gets platfrom specific data accordingly to `Adobe Analytics Mapping Data Sheet>`
     */
    _getPlatformDetauks() {
        const { platform } = ConfigHelper.getInstance().getConfig()
        switch (platform) {
            case Constants.platform.androidTvStb:
                return {
                    platform: 'stb',
                    mediaPlayer: 'mewatch_stb_stbplayer',
                }
            case Constants.platform.android:
                return {
                    platform: 'mobileandroidphone',
                    mediaPlayer: 'mewatch_mobileandroidphone_androidplayer',
                }
            case Constants.platform.ios:
                return {
                    platform: 'mobileiphone',
                    mediaPlayer: 'mewatch_mobileiphone_iosplayer',
                }
            case Constants.platform.smartTvOs:
                return {
                    platform: 'tizentv',
                    mediaPlayer: 'mewatch_tizentv_tizenosplayer',
                }
            case Constants.platform.tvos:
                return {
                    platform: 'appletv',
                    mediaPlayer: 'mewatch_appletv_tvosplayer',
                }
            case Constants.platform.chromecast:
                return {
                    platform: 'mobileandroidphonechromecast',
                    mediaPlayer: 'mewatch_onlinechromecast_chromecastplayer',
                }
            case Constants.platform.chrome:
            case Constants.platform.firefox:
            case Constants.platform.edge:
            case Constants.platform.edgeChromium:
            case Constants.platform.safari:
                return {
                    platform: 'onlinechromecast',
                    mediaPlayer: 'mewatch_onlinechromecast_chromecastplayer',
                }
            case Constants.platform.androidTv:
            case Constants.platform.amazonTv:
            default:
                return {
                    platform: 'androidtv',
                    mediaPlayer: 'mewatch_androidtv_androidplayer',
                }
        }
    }

    /**
     * Checks if adobe analytics is enabled
     */
    _isEnabled() {
        const { enabled = false } = ConfigHelper.getInstance().getConfig('adobeAnalytics')
        return enabled
    }

    /**
     * Saves session id
     */
    _saveSessionId(sessionId: string) {
        this._sessionId = sessionId
    }

    /**
     * Gets session id
     */
    _getSessionId() {
        return this._sessionId
    }

    /**
     * Clears session id
     */
    _clearSessionId() {
        this._sessionId = undefined
    }
}
